import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getCookie } from "../components/Cookie.jsx";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";

// Define the Zod schema for validation
const schema = z.object({
  firstName: z
    .string()
    .min(2, "First Name must be at least 2 characters long")
    .nonempty("First Name is required")
    .regex(
      /^[A-Za-z]+$/,
      "First Name should only contain alphabetic characters"
    ),
  lastName: z
    .string()
    .min(2, "Last Name must be at least 2 characters long")
    .nonempty("Last Name is required")
    .regex(
      /^[A-Za-z]+$/,
      "Last Name should only contain alphabetic characters"
    ),
  email: z
    .string()
    .nonempty("Email Address is required")
    .email("Invalid email address")
    .refine((email) => email.includes("sciative") && !email.includes("gmail"), {
      message: "Email must include 'sciative'",
    }),
  accessGroup: z
    .string()
    .nonempty("Access Group is required")
    .transform((val) => parseInt(val, 10)),
});

const AdminAddUsers = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
  });

  const [accessGroupData, setAccessGroupData] = useState([]);

  useEffect(() => {
    getAccessGroups();
  }, []);

  const TOKEN = getCookie("sqa_token");

  // Handle form submission
  async function onSubmit(data) {
    const url = "/sqa/api/create_user";

    const encodedEmail = btoa(data.email);

    const payload = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: encodedEmail,
      role_id: data.accessGroup,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("New User was not Created");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      data = await response.json();

      if (data.success) {
        toast.success(data.message);
        reset();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("There was an error with the fetch operation:", error);
    }
  }

  const getAccessGroups = async () => {
    try {
      const url = `/sqa/api/get_access_groups/`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TOKEN}`,
        },
      });

      const data = await response.json();

      if (data?.result[0]) {
        setAccessGroupData(data?.result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <>
      <div className="px-20 font-semibold text-[16px] pt-4">User Details</div>
      <div className="p-4 px-20">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* First Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 font-semibold  pb-2"
              >
                First Name<span className="text-red-500 ">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                placeholder="Enter First Name"
                {...register("firstName")}
                className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 pb-2 font-semibold"
              >
                Last Name<span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                placeholder="Enter Last Name"
                {...register("lastName")}
                className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 pb-2 font-semibold"
              >
                Email Address<span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="text"
                placeholder="Enter Email"
                {...register("email")}
                className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="accessGroup"
                className="block text-sm font-medium text-gray-700 pb-2 font-semibold"
              >
                Access Group<span className="text-red-500">*</span>
              </label>
              <select
                id="accessGroup"
                {...register("accessGroup")}
                className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
              >
                <option value="">Select Access Group</option>
                {accessGroupData?.map((group) => (
                  <option key={group._id} value={group._id}>
                    {group.role_name}
                  </option>
                ))}
              </select>
              {errors.accessGroup && (
                <p className="text-red-500 text-sm">
                  {errors.accessGroup.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-center py-10">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdminAddUsers;
